import type { Feature, FeatureCollection, Polygon } from 'geojson'
import type { IPOI } from '@/shared/api/types/poi-types'
import type { ITourPoi } from '@/shared/api/types/tour-types'
import { normalizeCoordinates } from '@/features/map-drawing'

// Re-export all generic utilities so existing imports keep working
export type { MapCoordinate as TourRouteCoordinate } from '@/features/map-drawing'
export {
  appendCoordinate,
  createBoundaryGeoJson,
  fromMapboxLineString,
  getBoundaryBounds,
  insertCoordinateAt,
  isFiniteCoordinatePair,
  mergeBounds,
  midpoint,
  normalizeCoordinates as normalizeTourCoordinates,
  removeCoordinateAt,
  toMapboxLineString,
  toLineFeatureCollection as toRouteFeatureCollection,
  updateCoordinateAt,
} from '@/features/map-drawing'

export interface TourRouteEditState {
  coordinates: [number, number][]
  isDirty: boolean
}

const EARTH_RADIUS_KM = 6371

function toRadians(value: number): number {
  return (value * Math.PI) / 180
}

function haversineDistanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS_KM * c * 1000
}

function createGeoJsonCircle(
  latitude: number,
  longitude: number,
  radiusMeters: number,
  points = 64,
): FeatureCollection<Polygon> {
  const distanceX = radiusMeters / 1000 / (111.32 * Math.cos((latitude * Math.PI) / 180))
  const distanceY = radiusMeters / 1000 / 110.574

  const ring = Array.from({ length: points }, (_, index) => {
    const theta = (index / points) * (2 * Math.PI)
    const x = distanceX * Math.cos(theta)
    const y = distanceY * Math.sin(theta)
    return [longitude + x, latitude + y]
  })

  const first = ring[0]
  if (first) {
    ring.push(first)
  }

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [ring] },
      },
    ],
  }
}

export function poiRangesToFeatureCollection(
  selectedPois: ITourPoi[],
  poiMap: Record<string, IPOI>,
): FeatureCollection<Polygon> | null {
  const features: Feature<Polygon>[] = []
  for (const item of selectedPois) {
    const poi = poiMap[item.poiId]
    if (!poi) continue
    const radius = Number(poi.range)
    if (!Number.isFinite(radius) || radius <= 0) continue
    const fc = createGeoJsonCircle(poi.latitude, poi.longitude, radius)
    const feature = fc.features[0]
    if (feature) features.push(feature)
  }
  if (features.length === 0) return null
  return { type: 'FeatureCollection', features }
}

export function getPoiCoordinatesFromSelection(
  selectedPois: ITourPoi[],
  poiMap: Record<string, IPOI>,
): [number, number][] {
  return selectedPois
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((item) => {
      const poi = poiMap[item.poiId]
      if (!poi) return null
      return [poi.latitude, poi.longitude] as [number, number]
    })
    .filter((item): item is [number, number] => item !== null)
}

export function getRouteBounds(
  route: [number, number][],
  poiPositions: [number, number][],
): [[number, number], [number, number]] | null {
  const all = [...normalizeCoordinates(route), ...normalizeCoordinates(poiPositions)]
  const first = all[0]
  if (!first) return null

  let minLat = first[0]
  let maxLat = first[0]
  let minLng = first[1]
  let maxLng = first[1]

  all.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  })

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

export function isPoiNearRoute(
  poi: [number, number],
  route: [number, number][],
  thresholdMeters = 80,
): boolean {
  const normalizedRoute = normalizeCoordinates(route)
  if (normalizedRoute.length < 2) return false
  const [poiLat, poiLng] = poi
  return normalizedRoute.some(
    ([lat, lng]) => haversineDistanceMeters(poiLat, poiLng, lat, lng) <= thresholdMeters,
  )
}

export function validateDestinationCenter(destinationCenter: [unknown, unknown]): boolean {
  if (!destinationCenter) return false
  const [lat, lng] = destinationCenter
  return (
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lng === 'number' &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180
  )
}
